import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Crosshair,
  Download,
  FileText,
  Film,
  Image as ImageIcon,
  Music,
  Replace,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import { useRef, useState, type FormEvent, type MouseEvent } from 'react';

import {
  mediaDelete,
  mediaRestore,
  mediaUpdate,
  mediaRequeueHLS,
  mediaUsage,
} from '@/shared/api/generated/media/media';
import type { AssetBody } from '@/shared/api/generated/model';
import { fetchBlobURL } from '@/shared/api/binary';
import { ApiError } from '@/shared/api/http';
import { describeDate } from '@/shared/lib/dates';
import { pluralize } from '@/shared/lib/plural';
import { AuthImage } from '@/shared/ui/AuthImage';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog, Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { toast } from '@/shared/ui/Toast';

import { humanSize, kindTitle } from './format';
import styles from './MediaLibrary.module.scss';
import { replaceFile, type Environment } from './useUploader';
import { MediaPlayer, PosterSection } from './VideoTools';

/**
 * Карточка файла: превью (видео и аудио — сразу проигрыватель), имя и
 * подпись, точка фокуса у картинки, постер у видео — в том числе из кадра, «где используется». Удаление предупреждает, если файл где-то стоит:
 * «стоит в 3 записях — на сайте там будет пусто».
 */
export function AssetDetails({
  asset,
  siteId,
  env,
  canWrite,
  canRemove,
  onClose,
  onChanged,
  onPurge,
  pickPoster,
}: {
  asset: AssetBody;
  siteId: string;
  env: Environment;
  canWrite: boolean;
  canRemove: boolean;
  onClose: () => void;
  onChanged: () => void;
  /** Удалить навсегда — из корзины; спрашивает подтверждение медиатека. */
  onPurge: () => void;
  /** Выбрать постер видео — окно медиатеки в режиме выбора картинки. */
  pickPoster: (done: (poster: AssetBody) => void) => void;
}) {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState(false);
  // Замена файла: выбранный файл ждёт подтверждения, sent — ход загрузки.
  const [replacing, setReplacing] = useState<{ file: File; sent: number }>();
  const replaceInput = useRef<HTMLInputElement>(null);

  const usage = useQuery({
    queryKey: ['media-usage', asset.id],
    queryFn: async () => {
      const response = await mediaUsage(asset.id);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос использования');
      }

      return response.data.items ?? [];
    },
  });

  const save = (body: Parameters<typeof mediaUpdate>[1], done: string) => {
    mediaUpdate(asset.id, body, { 'If-Match': asset.etag })
      .then(() => {
        toast.success(done);
        onChanged();
        void queryClient.invalidateQueries({ queryKey: ['media-item', asset.id] });
      })
      .catch((cause: unknown) => {
        toast.error('Не удалось сохранить', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      });
  };

  const places = usage.data ?? [];
  const deleted = asset.deleted_at != null;

  return (
    <aside className={styles['details']} aria-label={`Файл «${asset.name}»`}>
      <header className={styles['details__head']}>
        <span className={styles['details__kind']}>{kindTitle(asset.kind)}</span>
        <Button
          iconOnly
          size="sm"
          variant="ghost"
          tone="neutral"
          aria-label="Закрыть"
          onClick={onClose}
        >
          <X size={15} />
        </Button>
      </header>

      <Preview
        asset={asset}
        canWrite={canWrite && !deleted}
        onFocus={(x, y) => {
          save({ focus: { x, y } }, 'Точка фокуса сохранена');
        }}
      />

      {asset.kind === 'image' && canWrite && !deleted && (
        <p className={styles['hint']}>
          <Crosshair size={13} /> Щёлкните по главному на картинке — при обрезке под разные экраны
          оно не отрежется.
          {asset.focus_x != null && (
            <button
              type="button"
              className={styles['link']}
              onClick={() => {
                save({ clear_focus: true }, 'Точка фокуса снята');
              }}
            >
              Снять
            </button>
          )}
        </p>
      )}

      <dl className={styles['facts']}>
        <dt>Размер</dt>
        <dd>
          {humanSize(asset.size)}
          {asset.optimized && asset.original_size != null && asset.original_size > asset.size && (
            <span className={styles['saved']}>
              {' '}
              сжато с {humanSize(asset.original_size)} (−
              {Math.round((1 - asset.size / asset.original_size) * 100)}%)
            </span>
          )}
        </dd>
        {asset.width != null && asset.height != null && (
          <>
            <dt>Стороны</dt>
            <dd>
              {asset.width}×{asset.height}
            </dd>
          </>
        )}
        {asset.duration != null && (
          <>
            <dt>Длительность</dt>
            <dd>{duration(asset.duration)}</dd>
          </>
        )}
        {asset.uploader != null && (
          <>
            <dt>Загрузил</dt>
            <dd>{asset.uploader}</dd>
          </>
        )}
        {asset.purge_at != null && (
          <>
            <dt>Удалится</dt>
            <dd>{describeDate(asset.purge_at.slice(0, 10))} — навсегда, сам</dd>
          </>
        )}
      </dl>

      {canWrite && !deleted && (
        <TextEdit key={`${asset.id}:${asset.etag}`} asset={asset} onSave={save} />
      )}

      {asset.kind === 'video' && !deleted && (
        <VideoStream asset={asset} canWrite={canWrite} onChanged={onChanged} />
      )}

      {asset.kind === 'video' && canWrite && !deleted && (
        <PosterSection
          asset={asset}
          onChanged={() => {
            onChanged();
            void queryClient.invalidateQueries({ queryKey: ['media-item', asset.id] });
          }}
          onPickFromLibrary={() => {
            pickPoster((poster) => {
              save({ poster_id: poster.id }, 'Постер выбран');
            });
          }}
          onClear={() => {
            save({ clear_poster: true }, 'Постер снят');
          }}
        />
      )}

      <div className={styles['usage']}>
        <span className={styles['label']}>Где используется</span>
        {usage.isPending ? (
          <p className={styles['hint']}>Считаем…</p>
        ) : places.length === 0 ? (
          <p className={styles['hint']}>Ни в одной записи. Можно смело убирать.</p>
        ) : (
          <ul className={styles['usage__list']}>
            {places.map((place) => (
              <li key={place.record_id}>
                <Link
                  to="/sites/$siteId/$env/r/$recordId"
                  params={{
                    siteId,
                    env: env === 'production' ? 'prod' : 'dev',
                    recordId: place.record_id,
                  }}
                  className={styles['link']}
                >
                  {place.title === '' ? 'Без названия' : place.title}
                </Link>{' '}
                <span className={styles['hint']}>
                  · {place.type_name}
                  {place.published ? ' · на сайте' : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles['details__actions']}>
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          iconLeft={<Download size={14} />}
          onClick={() => {
            // Файл отдаётся только со входом: обычная ссылка токена не
            // пошлёт, поэтому — скачать с токеном и отдать браузеру.
            fetchBlobURL(asset.url)
              .then((url) => {
                const link = document.createElement('a');

                link.href = url;
                link.download = asset.name;
                link.click();
                setTimeout(() => {
                  URL.revokeObjectURL(url);
                }, 10_000);
              })
              .catch(() => {
                toast.error('Не удалось скачать');
              });
          }}
        >
          Скачать
        </Button>

        {canRemove && deleted && (
          <>
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              iconLeft={<RotateCcw size={14} />}
              onClick={() => {
                mediaRestore(asset.id)
                  .then(() => {
                    toast.success(`«${asset.name}» возвращён`);
                    onChanged();
                  })
                  .catch(() => {
                    toast.error('Не удалось вернуть');
                  });
              }}
            >
              Вернуть
            </Button>
            <Button
              size="sm"
              variant="ghost"
              tone="danger"
              iconLeft={<Trash2 size={14} />}
              onClick={onPurge}
            >
              Удалить навсегда
            </Button>
          </>
        )}

        {canRemove && !deleted && (
          <>
            <Button
              size="sm"
              variant="outline"
              tone="neutral"
              iconLeft={<Replace size={14} />}
              onClick={() => {
                replaceInput.current?.click();
              }}
            >
              Заменить файл
            </Button>
            <input
              ref={replaceInput}
              type="file"
              hidden
              accept={ACCEPT[asset.kind]}
              onChange={(event) => {
                const file = event.target.files?.[0];

                event.target.value = '';

                if (file !== undefined) {
                  setReplacing({ file, sent: 0 });
                }
              }}
            />
          </>
        )}

        {canRemove && !deleted && (
          <Button
            size="sm"
            variant="ghost"
            tone="danger"
            iconLeft={<Trash2 size={14} />}
            onClick={() => {
              setRemoving(true);
            }}
          >
            В корзину
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={replacing !== undefined}
        onOpenChange={(open) => {
          if (!open) setReplacing(undefined);
        }}
        title={`Заменить «${asset.name}»?`}
        description={
          places.length === 0
            ? `Встанет «${replacing?.file.name ?? ''}» — под тем же именем, с той же подписью и точкой фокуса.`
            : `«${replacing?.file.name ?? ''}» встанет во всех ${pluralize(places.length, ['записи', 'записях', 'записях'])}, где стоит этот файл, и на сайте. Имя, подпись и точка фокуса останутся.`
        }
        confirmLabel="Заменить"
        tone="accent"
        onConfirm={async () => {
          if (replacing === undefined) return;

          try {
            await replaceFile(siteId, env, asset.id, replacing.file, (sent) => {
              setReplacing((prev) => (prev === undefined ? prev : { ...prev, sent }));
            });
            toast.success('Файл заменён', {
              description: places.length > 0 ? 'Новый уже во всех записях.' : 'Старый стёрт.',
            });
            onChanged();
            void queryClient.invalidateQueries({ queryKey: ['media-item', asset.id] });
            void queryClient.invalidateQueries({ queryKey: ['media-playback', asset.id] });
          } catch (cause) {
            toast.error('Не удалось заменить', {
              description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
            });

            throw cause;
          }
        }}
      >
        {replacing !== undefined && replacing.sent > 0 && replacing.sent < replacing.file.size && (
          <progress
            className={styles['stream__progress']}
            value={replacing.sent}
            max={replacing.file.size}
          />
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title={`Убрать «${asset.name}» в корзину?`}
        description={
          places.length === 0
            ? 'Файл ни в одной записи не стоит. Вернуть можно из корзины.'
            : `Файл стоит в ${pluralize(places.length, ['записи', 'записях', 'записях'])} — на сайте там будет пусто. Вернуть можно из корзины.`
        }
        confirmLabel="В корзину"
        tone="danger"
        onConfirm={async () => {
          await mediaDelete(asset.id);
          toast.success(`«${asset.name}» в корзине`);
          onChanged();
          onClose();
        }}
      />
    </aside>
  );
}

/** Превью; у картинки — с точкой фокуса, которую ставят щелчком. */
function Preview({
  asset,
  canWrite,
  onFocus,
}: {
  asset: AssetBody;
  canWrite: boolean;
  onFocus: (x: number, y: number) => void;
}) {
  if ((asset.kind === 'video' || asset.kind === 'audio') && asset.deleted_at == null) {
    return <MediaPlayer asset={asset} />;
  }

  if (asset.kind === 'image' && asset.thumb_url != null) {
    const pick = (event: MouseEvent<HTMLButtonElement>) => {
      const box = event.currentTarget.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
      const y = Math.min(1, Math.max(0, (event.clientY - box.top) / box.height));

      onFocus(Math.round(x * 1000) / 1000, Math.round(y * 1000) / 1000);
    };

    return (
      <button
        type="button"
        className={styles['preview']}
        disabled={!canWrite}
        aria-label="Поставить точку фокуса"
        onClick={pick}
      >
        <AuthImage
          src={asset.thumb_url}
          alt={asset.alt === '' ? asset.name : asset.alt}
          className={styles['preview__image']}
        />
        {asset.focus_x != null && asset.focus_y != null && (
          <span
            className={styles['focus']}
            style={{
              left: `${String(asset.focus_x * 100)}%`,
              top: `${String(asset.focus_y * 100)}%`,
            }}
            aria-hidden="true"
          />
        )}
      </button>
    );
  }

  const Icon =
    asset.kind === 'video'
      ? Film
      : asset.kind === 'audio'
        ? Music
        : asset.kind === 'image'
          ? ImageIcon
          : FileText;

  return (
    <div className={styles['preview']}>
      <Icon size={32} />
    </div>
  );
}

function TextEdit({
  asset,
  onSave,
}: {
  asset: AssetBody;
  onSave: (body: { name?: string; alt?: string }, done: string) => void;
}) {
  const [name, setName] = useState(asset.name);
  const [alt, setAlt] = useState(asset.alt);

  const changed = name.trim() !== asset.name || alt.trim() !== asset.alt;

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (name.trim() === '') {
      return;
    }

    onSave(
      { name: name.trim(), ...(asset.kind === 'image' ? { alt: alt.trim() } : {}) },
      'Сохранено',
    );
  };

  return (
    <form className={styles['edit']} onSubmit={submit}>
      <Field label="Имя">
        <Input
          value={name}
          maxLength={255}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </Field>
      {asset.kind === 'image' && (
        <Field
          label="Подпись"
          hint="Для незрячих и на случай, если картинка не загрузится. У украшения — пусто."
        >
          <Input
            value={alt}
            maxLength={500}
            onChange={(event) => {
              setAlt(event.target.value);
            }}
          />
        </Field>
      )}
      {changed && (
        <Button size="sm" type="submit">
          Сохранить
        </Button>
      )}
    </form>
  );
}

function duration(seconds: number): string {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;

  return `${String(m)}:${String(s).padStart(2, '0')}`;
}

/** Что предлагать в окне выбора при замене: файл того же вида. */
const ACCEPT: Record<string, string> = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
  document: '',
};

/** Окно с названием новой папки или нового имени. */
export function NameDialog({
  open,
  title,
  initial,
  onOpenChange,
  onSave,
}: {
  open: boolean;
  title: string;
  initial: string;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <NameBody title={title} initial={initial} onOpenChange={onOpenChange} onSave={onSave} />
      )}
    </Dialog>
  );
}

function NameBody({
  title,
  initial,
  onOpenChange,
  onSave,
}: {
  title: string;
  initial: string;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(initial);

  return (
    <DialogContent
      title={title}
      size="sm"
      footer={
        <Button type="submit" form="name-dialog" disabled={name.trim() === ''}>
          Сохранить
        </Button>
      }
    >
      <form
        id="name-dialog"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(name.trim());
          onOpenChange(false);
        }}
      >
        <Field label="Название" required>
          <Input
            value={name}
            autoFocus
            maxLength={100}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </Field>
      </form>
    </DialogContent>
  );
}

const STREAM: Record<string, string> = {
  none: 'Не перекодировано: сайт получит файл целиком.',
  queued: 'В очереди на перекодировку.',
  processing: 'Перекодируется',
  ready: 'Готово: видео грузится кусками, качество — под скорость сети.',
  failed: 'Не получилось перекодировать',
};

/** Видео для сайта (HLS): состояние перекодировки и «перекодировать». */
function VideoStream({
  asset,
  canWrite,
  onChanged,
}: {
  asset: AssetBody;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const status = asset.hls_status;
  const working = status === 'queued' || status === 'processing';

  return (
    <div className={styles['poster']}>
      <span className={styles['label']}>Для сайта (HLS)</span>
      <p className={styles['hint']}>
        {STREAM[status] ?? status}
        {status === 'processing' && ` — ${String(asset.hls_progress)}%`}
        {status === 'failed' && asset.hls_error !== undefined && `: ${asset.hls_error}`}
      </p>
      {status === 'processing' && (
        <progress className={styles['stream__progress']} value={asset.hls_progress} max={100} />
      )}
      {canWrite && !working && (
        <div className={styles['poster__actions']}>
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            loading={busy}
            onClick={() => {
              setBusy(true);
              mediaRequeueHLS(asset.id)
                .then(() => {
                  toast.success('Видео поставлено в очередь');
                  onChanged();
                })
                .catch((cause: unknown) => {
                  toast.error('Не удалось', {
                    description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
                  });
                })
                .finally(() => {
                  setBusy(false);
                });
            }}
          >
            {status === 'none'
              ? 'Перекодировать'
              : status === 'failed'
                ? 'Попробовать ещё раз'
                : 'Перекодировать заново'}
          </Button>
        </div>
      )}
    </div>
  );
}
